import { useState, useEffect, useCallback } from 'react';
import { getMusicalDna } from '../api/authClient';
import { useAuth } from '../state/AuthContext';
import '../styles/musicalDna.css';

// Donut chart colors — vibrant, distinct
const DONUT_COLORS = [
  '#6366f1', // indigo
  '#ec4899', // pink
  '#f59e0b', // amber
  '#10b981', // emerald
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ef4444', // red
  '#14b8a6', // teal
  '#f97316', // orange
  '#a855f7', // purple
  '#64748b', // slate (Other)
];

// ---------------------------------------------------------------------------
// Canvas share-card renderer — produces a 1080×1920 image matching the UI
// ---------------------------------------------------------------------------

function renderShareCard(dna, displayName) {
  const W = 1080;
  const H = 1920;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Background
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#0c0c20');
  bg.addColorStop(1, '#14082e');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Subtle noise dots
  ctx.globalAlpha = 0.04;
  for (let i = 0; i < 600; i++) {
    ctx.fillStyle = '#fff';
    ctx.fillRect(Math.random() * W, Math.random() * H, 1.5, 1.5);
  }
  ctx.globalAlpha = 1;

  const possessive = displayName.endsWith('s') ? `${displayName}'` : `${displayName}'s`;
  const contentPad = 80;
  const contentW = W - contentPad * 2;

  // ── Measure content to distribute vertical space ──
  const genres = dna.genreBreakdown || [];
  const insights = [];
  if (dna.deepestCut) insights.push({ label: 'Deepest Cut', value: dna.deepestCut });
  if (dna.coreArtist) insights.push({ label: 'Core Artist', value: dna.coreArtist });
  if (dna.persona) insights.push({ label: 'Discovery Persona', value: dna.persona.label, desc: dna.persona.description });
  if (dna.unexpectedMatch) insights.push({ label: 'Unexpected Match', value: dna.unexpectedMatch });

  const heroH = 36 + 20 + 160 + 30 + 36; // title + small gap + big number + spacing + subtitle
  const donutH = genres.length > 0 ? 340 : 0;
  const statCardsH = 140;
  const insightCardH = 110;
  const insightDescCardH = 150;
  const insightsH = insights.reduce((sum, ins) => sum + (ins.desc ? insightDescCardH : insightCardH), 0) + (insights.length - 1) * 24;
  const watermarkH = 30;

  const fixedContentH = heroH + donutH + statCardsH + insightsH + watermarkH;
  const topPad = 140;
  const bottomPad = 80;
  const usableH = H - topPad - bottomPad;
  // Gaps between major sections: hero | donut | stats | insights | watermark
  const gapCount = genres.length > 0 ? 4 : 3;
  const totalGap = usableH - fixedContentH;
  const gap = Math.max(40, Math.floor(totalGap / gapCount));

  let y = topPad;

  // ── Hero: Title + Rarity (one visual group) ──
  ctx.textAlign = 'center';
  ctx.fillStyle = '#8888aa';
  ctx.font = "600 36px 'Space Grotesk', sans-serif";
  ctx.letterSpacing = '2px';
  ctx.fillText(`${possessive} Musical DNA`.toUpperCase(), W / 2, y + 30);
  ctx.letterSpacing = '0px';

  const rarityY = y + 36 + 20; // right below title with a small gap
  const rarityGrad = ctx.createLinearGradient(W / 2 - 200, rarityY, W / 2 + 200, rarityY + 120);
  rarityGrad.addColorStop(0, '#818cf8');
  rarityGrad.addColorStop(0.5, '#c084fc');
  rarityGrad.addColorStop(1, '#f472b6');
  ctx.fillStyle = rarityGrad;
  ctx.font = "700 160px 'Space Grotesk', sans-serif";
  ctx.fillText(`${dna.explorationPct}%`, W / 2, rarityY + 130);
  ctx.fillStyle = '#8888aa';
  ctx.font = "400 36px 'Inter', sans-serif";
  ctx.fillText(`You've explored more music than ${dna.explorationPct}% of users`, W / 2, rarityY + 185);
  y += heroH + gap;

  // ── Donut chart ──
  if (genres.length > 0) {
    const donutCx = 300;
    const donutCy = y + 170;
    const outerR = 155;
    const innerR = 95;
    let cum = 0;
    const arcs = genres.map((g, i) => {
      const start = cum * 3.6 * (Math.PI / 180);
      cum += g.pct;
      const end = cum * 3.6 * (Math.PI / 180);
      const color = g.genre === 'Other' ? DONUT_COLORS[DONUT_COLORS.length - 1] : DONUT_COLORS[i % (DONUT_COLORS.length - 1)];
      return { ...g, start, end, color };
    });

    for (const arc of arcs) {
      const gapAngle = genres.length > 1 ? 0.025 : 0;
      const s = arc.start + gapAngle;
      const e = arc.end - gapAngle;
      if (e <= s) continue;
      ctx.beginPath();
      ctx.arc(donutCx, donutCy, outerR, s - Math.PI / 2, e - Math.PI / 2);
      ctx.arc(donutCx, donutCy, innerR, e - Math.PI / 2, s - Math.PI / 2, true);
      ctx.closePath();
      ctx.fillStyle = arc.color;
      ctx.globalAlpha = 0.85;
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // Legend
    const legendX = donutCx + outerR + 60;
    const legendLineH = 46;
    const legendTotalH = arcs.length * legendLineH;
    let legendY = donutCy - legendTotalH / 2 + legendLineH / 2;
    const pctX = W - 100;
    ctx.textAlign = 'left';
    for (const arc of arcs) {
      ctx.fillStyle = arc.color;
      ctx.beginPath();
      ctx.arc(legendX, legendY, 9, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#e8e8f0';
      ctx.font = "500 30px 'Inter', sans-serif";
      ctx.fillText(arc.genre, legendX + 24, legendY + 10);
      ctx.fillStyle = '#555577';
      ctx.font = "400 28px 'Inter', sans-serif";
      ctx.textAlign = 'right';
      ctx.fillText(`${arc.pct}%`, pctX, legendY + 10);
      ctx.textAlign = 'left';
      legendY += legendLineH;
    }

    y += donutH + gap;
  }

  // ── Stat cards ──
  const cardGap = 40;
  const cardW = (contentW - cardGap) / 2;
  const cardH = 140;
  const leftX = contentPad;
  const rightX = contentPad + cardW + cardGap;

  function drawStatCard(x, yy, value, label) {
    ctx.fillStyle = 'rgba(255,255,255,0.04)';
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(x, yy, cardW, cardH, 20);
    ctx.fill();
    ctx.stroke();
    ctx.textAlign = 'center';
    ctx.fillStyle = '#e8e8f0';
    ctx.font = "700 54px 'Space Grotesk', sans-serif";
    ctx.fillText(value, x + cardW / 2, yy + 65);
    ctx.fillStyle = '#555577';
    ctx.font = "600 22px 'Space Grotesk', sans-serif";
    ctx.letterSpacing = '1px';
    ctx.fillText(label.toUpperCase(), x + cardW / 2, yy + 110);
    ctx.letterSpacing = '0px';
  }

  drawStatCard(leftX, y, String(dna.totalArtists), 'Total Explored');
  drawStatCard(rightX, y, `${dna.discoveryPct}%`, 'Discovery Rate');
  y += statCardsH + gap;

  // ── Insight rows ──
  const insightW = contentW;
  const insightX = contentPad;

  for (const ins of insights) {
    const h = ins.desc ? insightDescCardH : insightCardH;
    ctx.fillStyle = 'rgba(255,255,255,0.03)';
    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(insightX, y, insightW, h, 20);
    ctx.fill();
    ctx.stroke();
    ctx.textAlign = 'left';
    ctx.fillStyle = '#555577';
    ctx.font = "600 22px 'Space Grotesk', sans-serif";
    ctx.letterSpacing = '1px';
    ctx.fillText(ins.label.toUpperCase(), insightX + 32, y + 40);
    ctx.letterSpacing = '0px';
    ctx.fillStyle = '#e8e8f0';
    ctx.font = "600 40px 'Space Grotesk', sans-serif";
    ctx.fillText(ins.value, insightX + 32, y + 82);
    if (ins.desc) {
      ctx.fillStyle = '#8888aa';
      ctx.font = "400 28px 'Inter', sans-serif";
      ctx.fillText(ins.desc, insightX + 32, y + 124);
    }
    y += h + 24;
  }

  // ── Stellar branding footer ──
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(255,255,255,0.8)';
  ctx.font = "700 40px 'Space Grotesk', sans-serif";
  ctx.letterSpacing = '2px';
  ctx.fillText('Stellar', W / 2, H - 100);
  ctx.letterSpacing = '0px';

  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.font = "400 26px 'Inter', sans-serif";
  ctx.fillText('stellarmusic.io', W / 2, H - 60);

  return canvas;
}

function GenreDonut({ genres }) {
  if (!genres || genres.length === 0) return null;

  const size = 160;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = 68;
  const innerR = 42;

  // Build arcs
  let cumulative = 0;
  const arcs = genres.map((g, i) => {
    const startAngle = cumulative * 3.6 * (Math.PI / 180); // pct to degrees to radians
    cumulative += g.pct;
    const endAngle = cumulative * 3.6 * (Math.PI / 180);
    const color = g.genre === 'Other' ? DONUT_COLORS[DONUT_COLORS.length - 1] : DONUT_COLORS[i % (DONUT_COLORS.length - 1)];
    return { ...g, startAngle, endAngle, color };
  });

  return (
    <div className="dna-donut-wrap">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {arcs.map((arc, i) => {
          const largeArc = arc.pct > 50 ? 1 : 0;

          // Gap between slices
          const gap = genres.length > 1 ? 0.02 : 0;
          const gapStartAngle = arc.startAngle + gap;
          const gapEndAngle = arc.endAngle - gap;

          if (gapEndAngle <= gapStartAngle) return null;

          const gx1o = cx + outerR * Math.sin(gapStartAngle);
          const gy1o = cy - outerR * Math.cos(gapStartAngle);
          const gx2o = cx + outerR * Math.sin(gapEndAngle);
          const gy2o = cy - outerR * Math.cos(gapEndAngle);
          const gx1i = cx + innerR * Math.sin(gapEndAngle);
          const gy1i = cy - innerR * Math.cos(gapEndAngle);
          const gx2i = cx + innerR * Math.sin(gapStartAngle);
          const gy2i = cy - innerR * Math.cos(gapStartAngle);

          const d = [
            `M ${gx1o} ${gy1o}`,
            `A ${outerR} ${outerR} 0 ${largeArc} 1 ${gx2o} ${gy2o}`,
            `L ${gx1i} ${gy1i}`,
            `A ${innerR} ${innerR} 0 ${largeArc} 0 ${gx2i} ${gy2i}`,
            'Z',
          ].join(' ');

          return <path key={i} d={d} fill={arc.color} opacity="0.85" />;
        })}
      </svg>
      <div className="dna-donut-legend">
        {arcs.map((arc, i) => (
          <div key={i} className="dna-donut-legend-item">
            <span className="dna-donut-legend-dot" style={{ background: arc.color }} />
            <span className="dna-donut-legend-label">{arc.genre}</span>
            <span className="dna-donut-legend-pct">{arc.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MusicalDnaDrawer({ onClose }) {
  const { user } = useAuth();
  const [dna, setDna] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMusicalDna()
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        if (data.error) {
          setError(data.error);
        } else {
          setDna(data);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load Musical DNA');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const displayName = user?.displayName || 'Your';
  const possessiveName = displayName.endsWith('s') ? `${displayName}'` : `${displayName}'s`;

  const [sharing, setSharing] = useState(false);

  const handleShare = useCallback(async () => {
    if (!dna) return;
    setSharing(true);
    try {
      const card = renderShareCard(dna, displayName);
      const blob = await new Promise((resolve) => card.toBlob(resolve, 'image/png'));
      const file = new File([blob], 'musical-dna.png', { type: 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `${displayName}'s Musical DNA`,
          files: [file],
        });
      } else if (navigator.share) {
        // Fallback: share URL + text (no image support)
        await navigator.share({
          title: `${displayName}'s Musical DNA`,
          text: `I've explored more music than ${dna.explorationPct}% of users \u00b7 ${dna.totalArtists} artists explored\nstellarmusic.io`,
        });
      } else {
        // Desktop fallback: download the image
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'musical-dna.png';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch { /* user cancelled or error */ }
    setSharing(false);
  }, [dna, displayName]);

  return (
    <div className="dna-drawer">
      <button className="dna-drawer-close" onClick={onClose}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <div className="dna-drawer-content">
        {loading && (
          <div className="dna-loading">
            <div className="dna-loading-spinner" />
            <span>Analyzing your Musical DNA...</span>
          </div>
        )}

        {error && !loading && (
          <div className="dna-error">
            <span>{error}</span>
            <button className="dna-retry-btn" onClick={() => window.location.reload()}>Retry</button>
          </div>
        )}

        {dna && !loading && (
          <>
            {/* Hero — Taste Rarity */}
            <div className="dna-hero">
              <h3 className="dna-hero-title">{possessiveName} Musical DNA</h3>
              <div className="dna-rarity">
                <span className="dna-rarity-value">{dna.explorationPct}%</span>
                <span className="dna-rarity-label">You've explored more music than {dna.explorationPct}% of users</span>
              </div>
            </div>

            {/* Middle — Genre Donut + Stats */}
            <div className="dna-middle">
              <GenreDonut genres={dna.genreBreakdown} />

              <div className="dna-stats-row">
                <div className="dna-stat-card">
                  <span className="dna-stat-value">{dna.totalArtists}</span>
                  <span className="dna-stat-label">Total Explored</span>
                </div>
                <div className="dna-stat-card">
                  <span className="dna-stat-value">{dna.discoveryPct}%</span>
                  <span className="dna-stat-label">Discovery Rate</span>
                </div>
              </div>
            </div>

            {/* Bottom — Punchy Stats */}
            <div className="dna-bottom">
              {dna.deepestCut && (
                <div className="dna-insight">
                  <span className="dna-insight-label">Deepest Cut</span>
                  <span className="dna-insight-value">{dna.deepestCut}</span>
                </div>
              )}
              {dna.coreArtist && (
                <div className="dna-insight">
                  <span className="dna-insight-label">Core Artist</span>
                  <span className="dna-insight-value">{dna.coreArtist}</span>
                </div>
              )}
              {dna.persona && (
                <div className="dna-insight">
                  <span className="dna-insight-label">Discovery Persona</span>
                  <span className="dna-insight-value">{dna.persona.label}</span>
                  <span className="dna-insight-desc">{dna.persona.description}</span>
                </div>
              )}
              {dna.unexpectedMatch && (
                <div className="dna-insight">
                  <span className="dna-insight-label">Unexpected Match</span>
                  <span className="dna-insight-value">{dna.unexpectedMatch}</span>
                </div>
              )}
            </div>

            {/* Share */}
            <div className="dna-actions">
              <button className="dna-share-btn" onClick={handleShare} disabled={sharing}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
                {sharing ? 'Generating...' : 'Share My DNA'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MusicalDnaDrawer;
